import { HttpBackend, HttpClient } from "@angular/common/http";
import { ChangeDetectionStrategy, Component, Inject, Input, OnInit } from "@angular/core";
import { LocalComponentStore, Logger } from "@dtm-frontend/shared/utils";
import { RasterSourceSpecification, StyleSpecification } from "@maplibre/maplibre-gl-style-spec";
import { Observable, firstValueFrom, of, throwError } from "rxjs";
import { catchError, first, shareReplay } from "rxjs/operators";
import { SHARED_MAP_ENDPOINTS, SharedMapEndpoints } from "../../../shared";
import { MapLayerProviderComponent } from "../map-layer-provider/map-layer-provider.component";
import { GeotilerImageryProvider, GeotilerImageryProviderOptions } from "./geotiler-imagery.provider";

type RasterTilesStyle = StyleSpecification & { sources: Record<string, RasterSourceSpecification> };

@Component({
    selector: "dtm-map-geotiler-maps-layer:not([provider]):not([options])",
    template: "",
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [LocalComponentStore],
})
export class GeotilerMapsLayerComponent extends MapLayerProviderComponent<GeotilerImageryProviderOptions> implements OnInit {
    // NOTE: will be set in ngOnInit, overwriting required flag from base class
    @Input({ required: false }) public options!: GeotilerImageryProviderOptions;
    @Input({ required: false }) public provider = GeotilerImageryProvider;
    private readonly httpClient: HttpClient;
    private readonly styles$: Observable<RasterTilesStyle | null>;

    constructor(httpBackend: HttpBackend, @Inject(SHARED_MAP_ENDPOINTS) private readonly endpoints: SharedMapEndpoints) {
        super();
        // NOTE: little hack to avoid default interceptors and make clear request
        this.httpClient = new HttpClient(httpBackend);

        this.styles$ = this.prepareStylesObservable();
    }

    public async ngOnInit() {
        this.options = await this.getImageryProviderOptions();
        this.rebuildLayer();
    }

    private prepareStylesObservable(): Observable<RasterTilesStyle | null> {
        if (!this.endpoints.geotilerStylesUrl) {
            return of(null);
        }

        return this.httpClient.get<RasterTilesStyle>(this.endpoints.geotilerStylesUrl).pipe(
            catchError((error) => {
                Logger.captureMessage("MapTilesService.prepareStylesObservable: map tiles styles cannot be properly loaded", {
                    level: "error",
                    extra: { error },
                });

                return throwError(() => error);
            }),
            first(),
            shareReplay({ bufferSize: 1, refCount: true })
        );
    }

    private async getImageryProviderOptions(): Promise<GeotilerImageryProviderOptions> {
        const styles = await firstValueFrom(this.styles$);

        return {
            url: styles?.sources.base.tiles ? styles?.sources.base.tiles[0] : "",
            credit: styles?.sources.base.attribution ?? "",
        };
    }
}
